import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { CollinsUser } from "../types/user";
import { VenueGroupConfig } from "../types/VenueGroup";
import { getEnvironment, isBrowser, isNativeTab } from "../utils/environment";

declare global {
  interface Window {
    Intercom: (command: string, options?: IntercomSettings | object) => void;
  }
}

type SignedInVia = "workspace" | "direct link" | "mobile app";

interface IntercomSettings {
  app_id: string;
  user_id?: string;
  companies?: Array<{
    company_id: string;
    name: string;
    created_at?: number;
  }>;
  name?: string;
  email?: string;
  intercom_user_jwt?: string;
  created_at?: number;
  site?: string;
  last_request_at?: number;
  collins_evo_enabled?: boolean;
  evo_enabled?: boolean;
  signed_in_via?: SignedInVia;
}

interface UseIntercomProps {
  user?: CollinsUser;
  venueGroupConfig?: VenueGroupConfig | null;
  isAuthenticated: boolean;
}

function getSignedInVia(): SignedInVia {
  if (!isBrowser()) {
    return "mobile app";
  }

  if (isNativeTab()) {
    return "workspace";
  }

  return "direct link";
}

export const useIntercom = ({
  user,
  venueGroupConfig,
  isAuthenticated
}: UseIntercomProps): void => {
  const location = useLocation();
  const hasBooted = useRef(false);
  const hasUpdatedVenueGroup = useRef(false);

  useEffect(() => {
    const bootIntercom = async () => {
      if (!isAuthenticated || !user || hasBooted.current) {
        return;
      }

      try {
        const environment = await getEnvironment();
        const intercomAppId = environment?.INTERCOM_APP_ID;

        if (!intercomAppId || typeof window.Intercom !== "function") {
          return;
        }

        const companies: Array<{
          company_id: string;
          name: string;
          created_at?: number;
        }> = [];
        if (venueGroupConfig?._id) {
          companies.push({
            company_id: venueGroupConfig._id,
            name: venueGroupConfig.title ?? "",
            created_at: venueGroupConfig.created_date
              ? Math.floor(
                  new Date(venueGroupConfig.created_date).getTime() / 1000
                )
              : undefined
          });
          hasUpdatedVenueGroup.current = true;
        }

        const createdAt = user.created_date
          ? Math.floor(new Date(user.created_date).getTime() / 1000)
          : undefined;

        const collinsEvoEnabled = venueGroupConfig?.product_tier === "evo";

        window.Intercom("boot", {
          app_id: intercomAppId,
          user_id: user.workspace_id,
          intercom_user_jwt: user.intercom_user_jwt,
          companies,
          name: `${user.first_name ?? ""} ${user.last_name ?? ""}`.trim(),
          email: user.email,
          created_at: createdAt,
          collins_evo_enabled: collinsEvoEnabled,
          evo_enabled: !!venueGroupConfig?.workspace_organisation_id,
          signed_in_via: getSignedInVia()
        });

        hasBooted.current = true;
      } catch (error) {
        console.error("Failed to boot Intercom", error);
      }
    };

    bootIntercom();
  }, [isAuthenticated, user, venueGroupConfig]);

  useEffect(() => {
    if (
      !hasBooted.current ||
      hasUpdatedVenueGroup.current ||
      !venueGroupConfig?._id ||
      typeof window.Intercom !== "function"
    ) {
      return;
    }

    const companies: Array<{
      company_id: string;
      name: string;
      created_at?: number;
    }> = [];
    companies.push({
      company_id: venueGroupConfig._id,
      name: venueGroupConfig.title ?? "",
      created_at: venueGroupConfig.created_date
        ? Math.floor(new Date(venueGroupConfig.created_date).getTime() / 1000)
        : undefined
    });

    window.Intercom("update", {
      companies,
      collins_evo_enabled: venueGroupConfig.product_tier === "evo",
      evo_enabled: !!venueGroupConfig.workspace_organisation_id
    });

    hasUpdatedVenueGroup.current = true;
  }, [venueGroupConfig]);

  useEffect(() => {
    if (!hasBooted.current || typeof window.Intercom !== "function") {
      return;
    }

    const updateIntercom = async () => {
      const environment = await getEnvironment();
      const intercomAppId = environment?.INTERCOM_APP_ID;

      if (!intercomAppId) {
        return;
      }

      const pathParts = location.pathname.split("/");
      const siteIndex = pathParts.indexOf("sites");
      const venueId =
        siteIndex !== -1 && pathParts.length > siteIndex + 1
          ? pathParts[siteIndex + 1]
          : undefined;

      if (venueId) {
        window.Intercom("update", {
          app_id: intercomAppId,
          user_id: user?.workspace_id,
          site: venueId,
          email: user?.email
        });
      } else {
        window.Intercom("update", {
          last_request_at: Math.floor(Date.now() / 1000)
        });
      }
    };

    updateIntercom();
  }, [location.pathname, user?.workspace_id, user?.email]);
};
